import { formatGuidanceDistance } from './maps/routeGuidance';

/**
 * What a recorded pickup arrival means, said the same way on both sides.
 *
 * WHY THIS EXISTS
 *
 * The arrival is a record. When a Rider records arrival somewhere other than the
 * store, that fact is kept so anyone reviewing the trip afterwards can see it -
 * and so the staff member deciding whether to release parcels knows before they
 * decide, not after.
 *
 * A warning with no evidence in it cannot do either job. "Your arrival has a
 * location warning" is a sentence the Rider cannot act on and the store cannot
 * judge: 260 m with a poor fix and 5 km away read identically. So every message
 * here carries the measured distance or accuracy that produced it.
 *
 * The two sides need different words for the same fact - the Rider is being told
 * what will happen to them, the store is being told what to check - so the copy
 * is split rather than shared, while the numbers stay identical.
 */

export const ARRIVAL_VERIFIED = 'VERIFIED';
export const ARRIVAL_DISTANCE_WARNING = 'DISTANCE_WARNING';
export const ARRIVAL_ACCURACY_WARNING = 'ACCURACY_WARNING';

function metres(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

/**
 * @param {object} arrival the run's pickupArrival
 * @param {"rider"|"business"} audience who is reading it
 * @returns {{tone: "positive"|"caution", title: string, detail: string, evidence: string|null}|null}
 */
export function describePickupArrival(arrival, audience = 'rider') {
  if (!arrival) return null;
  const distanceM = metres(arrival.distanceM);
  const accuracyM = metres(arrival.accuracyM);
  const forRider = audience === 'rider';

  const evidence = [
    distanceM === null ? null : `${formatGuidanceDistance(distanceM)} from the store`,
    accuracyM === null ? null : `location accurate to about ${Math.round(accuracyM)} m`,
  ].filter(Boolean).join(' · ') || null;

  if (arrival.classification === ARRIVAL_VERIFIED) {
    return {
      tone: 'positive',
      title: forRider ? 'Arrival recorded at the store' : 'Rider arrived at the store',
      detail: forRider
        ? 'Staff can release your parcels once they have checked the manifest.'
        : 'The recorded position matches this store. Release the parcels when the manifest checks out.',
      evidence,
    };
  }

  if (arrival.classification === ARRIVAL_ACCURACY_WARNING) {
    return {
      tone: 'caution',
      title: forRider ? 'Your location was too rough to confirm' : 'Rider location was too rough to confirm',
      detail: forRider
        ? 'Your phone could not place you precisely, so this arrival is recorded as unconfirmed. Staff will check who you are in person before releasing anything - bring your ID.'
        : 'The phone could not place the Rider precisely, so this arrival is recorded as unconfirmed. It does not mean they are in the wrong place. Check who they are in person before releasing.',
      evidence,
    };
  }

  // Anything else that is not VERIFIED is a distance warning, including a
  // classification this build does not know about - which is still not a
  // confirmed arrival and must not read like one.
  return {
    tone: 'caution',
    title: forRider ? 'Recorded away from the store' : 'Rider recorded away from the store',
    detail: forRider
      ? 'This is kept on the trip record. Staff will check who you are in person before releasing anything, which can take longer - bring your ID.'
      : 'This is kept on the trip record. Check who the Rider is in person before releasing, and ask why they recorded arrival from there.',
    evidence,
  };
}
